import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { BrandMark, Modal, Picker, type PickerOption } from "@cntrl/ui";
import {
  useApplicantAccount,
  useApplyConfig,
  useApplyInstagramDraft,
  useApplyInstagramStart,
  useApplySpotifyLookup,
  useSubmitApplication,
  type ApplySpotifyArtist,
} from "../../app/hooks/useApply.js";
import { ApiError, formErrorMessage } from "../../app/hooks/apiError.js";
import { useAuth } from "../../app/auth/useAuth.js";
import { LoginScreen } from "../auth/LoginScreen.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { onOAuthComplete, openOAuthPopup, popupReturnPath } from "../../app/oauthPopup.js";
import { GenrePicker } from "./GenrePicker.js";
import { planKeyFor, type Billing, type Mode, type Product } from "./applyPlans.js";
import { Turnstile } from "./Turnstile.js";
import { ANTENNA_URL, APPLY_FONTS_CSS, INSTAGRAM_URL, MARKETING_HOME, TIKTOK_URL } from "./marketingLinks.js";
import logoBlack from "./assets/logo-lockup-redmark-black.svg?url";
import logoWhite from "./assets/logo-lockup-white.svg?url";
import cntrlGlobal from "./assets/cntrl-global-horizontal-white.svg?url";
import festival from "./assets/photo-festival.jpg";
import styles from "./ApplyPage.module.scss";

// The public application page, rebuilt from the approved marketing handoff (.design-specs/sources/apply/,
// .design-specs/apply.plan.md). It copies the handoff's look exactly — its own header, footer and fonts — because
// it is a marketing surface reached from the marketing site, not an app screen. Where this page differs from the
// handoff is recorded in the plan: no Trusted-by card, no promo code, the two emails + role (ART-307), Instagram
// verification, Cloudflare Turnstile.


const PLANS: ReadonlyArray<{ key: Product; name: string; annual: string; m2m: string; note: string }> = [
  { key: "std", name: "Artist CNTRL", annual: "$79", m2m: "$99", note: "Everything your camp needs, every seat included." },
  { key: "pro", name: "Artist CNTRL Pro", annual: "$150", m2m: "$175", note: "Built for artists playing 3K+ rooms." },
];


const TRACTION: ReadonlyArray<{ key: keyof Traction; q: string; opts: string[] }> = [
  { key: "spotifyListeners", q: "1K+ monthly listeners on Spotify?", opts: ["Yes", "No"] },
  { key: "instagramFollowers", q: "1K+ followers on Instagram?", opts: ["Yes", "No"] },
  { key: "playingShows", q: "Actively playing shows?", opts: ["Yes", "No"] },
  { key: "touringVehicle", q: "Touring vehicle", opts: ["Van & trailer", "Buses & trucks"] },
  { key: "roomSize", q: "Average room size", opts: ["500–1K", "1K–3K", "3K+"] },
  { key: "crewSize", q: "Crew size", opts: ["Under 20", "20–40", "40+"] },
];

interface Traction {
  spotifyListeners?: string;
  instagramFollowers?: string;
  playingShows?: string;
  touringVehicle?: string;
  roomSize?: string;
  crewSize?: string;
}

const TEAM = ["Manager", "Booking agent", "Tour manager", "Merch person", "FOH engineer", "Crew member"];
const ROSTER_SIZES = ["2–5", "6–15", "16–30", "31–75", "76–150", "150+"];
const SOCIALS = ["Instagram", "TikTok", "YouTube", "Twitter / X", "Facebook", "None yet"];
const STREAMING = ["Spotify", "Apple Music", "YouTube Music", "Amazon Music", "Bandcamp", "SoundCloud", "None yet"];
const COMPANY_TYPES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "label", label: "Label" },
  { key: "management", label: "Management" },
  { key: "booking", label: "Booking agency" },
];
const COMPANY_ROLES = ["Owner / founder", "Manager", "Booking agent", "A&R", "Operations", "Other"];

/** The non-band answers to "Your role" (ART-307). Picking one never invites the applicant into the band; a
 * signed-in Management applicant's company is linked to it instead. */
const ORG_ROLES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "management", label: "Management" },
  { key: "label", label: "Label" },
  { key: "booking", label: "Booking" },
];

/** The one band answer to "Your role": a band member applying is seated with full access, so the picker offers
 * no other band titles. */
const BAND_MEMBER_TITLE = "band_member_full";

interface InstagramState {
  draft: string;
  username: string;
  listedUsername?: string | null;
  matchesListed?: boolean | null;
}

export interface ApplyPageProps {
  initialMode?: Mode;
  initialProduct?: Product;
  /**
   * Show no price, plan or pricing link (ART-346). Set when the iOS app opened this page from a storefront outside
   * the US, where a page the app sends people to must not sell the subscription. The application then names no
   * plan; approval starts the trial on Standard monthly and the plan is chosen at checkout after the trial.
   */
  hidePricing?: boolean;
}

export function ApplyPage({ initialMode = "artist", initialProduct = "std", hidePricing = false }: ApplyPageProps) {
  useHandoffFonts();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href={MARKETING_HOME} className={styles.logoLink}>
          <img src={logoBlack} alt="Artist CNTRL" className={styles.logo} />
        </a>
        <nav className={styles.navLinks} aria-label="Artist CNTRL">
          <a href={`${MARKETING_HOME}#everything`} className={styles.navLink}>Features</a>
          <a href={`${MARKETING_HOME}#integrations`} className={styles.navLink}>Integrations</a>
          {!hidePricing && <a href={`${MARKETING_HOME}#pricing`} className={styles.navLink}>Pricing</a>}
        </nav>
        <div className={styles.headerActions}>
          <a href="/login" className={styles.loginBtn}>Log in</a>
          <a href="#apply" className={styles.applyBtn}>Apply</a>
        </div>
      </header>

      <section id="apply" className={styles.grid}>
        <div className={styles.left}>
          <aside className={styles.aside}>
            <img src={festival} alt="" aria-hidden="true" className={styles.asidePhoto} />
            <div aria-hidden="true" className={styles.asideShade} />
            <div className={styles.asideBody}>
              <h1 className={styles.h1}>Take CNTRL.</h1>
              <p className={styles.asideIntro}>Artists, managers, booking agents and labels are approved case by case.</p>
              <ol className={styles.steps}>
                <Step n="01">Fill out the form. Takes about 3 minutes.</Step>
                <Step n="02">We read it personally and reply within 48 hours.</Step>
                <Step n="03">Get your login and invite the whole team.</Step>
              </ol>
            </div>
          </aside>
        </div>

        <div className={styles.right}>
          <div className={styles.why}>
            <div aria-hidden="true" className={styles.whyGlow} />
            <div className={styles.whyBody}>
              <h2 className={styles.whyTitle}>Why an application?</h2>
              <p className={styles.whyLead}>We'd rather get it right than just get paid.</p>
              <div className={styles.whyCards}>
                <div className={styles.glass}>
                  <div className={styles.glassHead}>
                    <span className={styles.glassIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </span>
                    <span className={styles.glassTitle}>Worth it for you</span>
                  </div>
                  <p className={styles.glassText}>
                    We want to know Artist CNTRL will actually pay off for your camp before we take your money. If
                    it's not the right time yet, we'll tell you straight.
                  </p>
                </div>
                <div className={styles.glass}>
                  <div className={styles.glassHead}>
                    <span className={styles.glassIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z" />
                      </svg>
                    </span>
                    <span className={styles.glassTitle}>Better for everyone</span>
                  </div>
                  <p className={styles.glassText}>
                    We vet every team so the platform can't be abused. Real artists and the people who work with
                    them, no spam or fake accounts. That keeps it better for everyone on it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full width under the two cards (ART-307): the form tucked into the right column read as a sidebar. */}
      <div className={styles.formRow}>
        <ApplicationForm initialMode={initialMode} initialProduct={initialProduct} hidePricing={hidePricing} />
      </div>

      <Footer hidePricing={hidePricing} />
    </div>
  );
}

function Step({ n, children }: { n: string; children: ReactNode }) {
  return (
    <li className={styles.step}>
      <span className={styles.stepNum}>{n}</span>
      <span className={styles.stepText}>{children}</span>
    </li>
  );
}

// ── the form ───────────────────────────────────────────────────────────────────────────────────────────────

function ApplicationForm({
  initialMode,
  initialProduct,
  hidePricing,
}: {
  initialMode: Mode;
  initialProduct: Product;
  hidePricing: boolean;
}) {
  const config = useApplyConfig();
  const lookup = useApplySpotifyLookup();
  const igStart = useApplyInstagramStart();
  const igDraft = useApplyInstagramDraft();
  const submit = useSubmitApplication();

  const [mode, setMode] = useState<Mode>(initialMode);
  const [product, setProduct] = useState<Product>(initialProduct);
  const [billing, setBilling] = useState<Billing>("annual");

  // 01 (artist)
  const [accountName, setAccountName] = useState("");
  const [email, setEmail] = useState("");          // artist: the BAND's email · roster: the contact email
  const [myEmail, setMyEmail] = useState("");      // artist: the applicant's own (signed out)
  const [roleKey, setRoleKey] = useState("");
  const [linkOrgId, setLinkOrgId] = useState("");
  const [city, setCity] = useState("");
  const [spotifyLink, setSpotifyLink] = useState("");
  const [artist, setArtist] = useState<ApplySpotifyArtist | null>(null);
  const [artistError, setArtistError] = useState<string | null>(null);
  const [instagram, setInstagram] = useState<InstagramState | null>(null);
  const [igError, setIgError] = useState<ReactNode>(null);
  const [whyOpen, setWhyOpen] = useState(false);

  // 01 (roster)
  const [companyType, setCompanyType] = useState("label");
  const [companyRole, setCompanyRole] = useState(COMPANY_ROLES[0]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [website, setWebsite] = useState("");
  const [rosterSize, setRosterSize] = useState<string | null>(null);

  // 02–05
  const [genres, setGenres] = useState<string[]>([]);
  const [traction, setTraction] = useState<Traction>({});
  const [topSocial, setTopSocial] = useState(SOCIALS[0]);
  const [topStreaming, setTopStreaming] = useState(STREAMING[0]);
  const [team, setTeam] = useState<string[]>([]);

  const [token, setToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ emails: string[] } | null>(null);

  // "I already have an account" (ART-307): signed in, the account IS the applicant. Opened while signed in, the box
  // starts checked with the account shown.
  const auth = useAuth();
  const [signedIn, setSignedIn] = useState(() => auth.isSignedIn());
  const [haveAccount, setHaveAccount] = useState(signedIn);
  const [loginOpen, setLoginOpen] = useState(false);
  const account = useApplicantAccount(signedIn);
  const me = signedIn ? account.data : undefined;
  // An artist (band) account cannot apply as the applicant (PO, ART-307): the applicant is always a person.
  const artistSeat = me?.seats?.find((x) => x.kind === "band" && x.isOwner) ?? null;
  const managementOrgs = (me?.seats ?? [])
    .filter((x) => x.kind === "org" && x.root === "management" && x.orgId)
    .map((x) => ({ orgId: x.orgId!, name: x.displayName ?? "" }));

  const contactEmail = config.data?.contactEmail ?? "support@cntrlglobal.com";
  const orgRole = ORG_ROLES.find((r) => r.key === roleKey);
  const linking = mode === "artist" && roleKey === "management" && !!me;

  // The popup reports back over the opener / BroadcastChannel; only a Meta result is ours.
  useEffect(
    () =>
      onOAuthComplete((result) => {
        if (result.provider !== "meta") return;
        if (!result.ok || !result.draft) {
          setIgError(
            result.reason === "no_instagram" ? (
              <>
                We couldn't find an Instagram Creator or Business account on that login. Make sure your Instagram is
                a Creator or Business account and is linked to a Facebook Page, then try again. Still stuck?{" "}
                <a href={`mailto:${contactEmail}`}>Contact us here.</a>
              </>
            ) : (
              "Instagram wasn't connected. Try again."
            ),
          );
          return;
        }
        setIgError(null);
        igDraft
          .mutateAsync(result.draft)
          .then((d) =>
            setInstagram({
              draft: result.draft!,
              username: d.username ?? "",
              listedUsername: d.listedUsername,
              matchesListed: d.matchesListed,
            }),
          )
          .catch(() => setIgError("Instagram was connected, but we couldn't read it back. Connect it again."));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one listener for the page's lifetime
    [contactEmail],
  );

  const onSpotifyBlur = () => {
    const link = spotifyLink.trim();
    setArtist(null);
    setArtistError(null);
    if (!link) return;
    lookup
      .mutateAsync(link)
      .then((a) => setArtist(a))
      .catch((err) =>
        setArtistError(
          err instanceof ApiError && err.status === 429
            ? "Too many lookups. Wait a minute and try again."
            : formErrorMessage(err, "We couldn't find that artist on Spotify."),
        ),
      );
  };

  const onConnectInstagram = () => {
    setIgError(null);
    // Open synchronously, before any await, or the browser treats it as an unrequested popup and blocks it.
    const popup = openOAuthPopup();
    if (!popup) {
      setIgError("Your browser blocked the Instagram window. Allow pop-ups for this site, then try again.");
      return;
    }
    igStart
      .mutateAsync({ spotifyLink: spotifyLink.trim(), returnUrl: popupReturnPath() })
      .then((url) => {
        popup.location.href = url;
      })
      .catch((err) => {
        popup.close();
        setIgError(formErrorMessage(err, "Instagram verification isn't available right now."));
      });
  };

  const plan = PLANS.find((p) => p.key === product)!;
  const summary =
    mode === "roster"
      ? "Roster deal, set at review"
      : `${plan.name} (${billing === "annual" ? `${plan.annual}/mo annual` : `${plan.m2m}/mo`})`;

  const onSignedIn = () => {
    setLoginOpen(false);
    setSignedIn(true);
    setHaveAccount(true);
    setErrors(({ applicantEmail: _, ...rest }) => rest);
  };

  const onSignOut = () => {
    void auth.logout().finally(() => {
      setSignedIn(false);
      setLinkOrgId("");
      // Signing out clears the query cache; the form's own server inputs come straight back.
      void config.refetch();
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const next: Record<string, string> = {};
    if (!accountName.trim()) next.accountName = mode === "roster" ? "Enter your company's name." : "Enter your artist or band name.";
    if (!email.trim()) next.accountEmail = mode === "roster" ? "Enter your email." : "Enter the band's email.";
    if (mode === "artist") {
      const mine = (me?.person?.email ?? myEmail).trim().toLowerCase();
      if (haveAccount && !me) next.applicantEmail = "Log in, or uncheck “I already have an account”.";
      else if (artistSeat) next.applicantEmail = "Sign in with your own account, not the artist account.";
      else if (!mine) next.applicantEmail = "Enter your email.";
      else if (mine === email.trim().toLowerCase())
        next.applicantEmail = "Use a different address from the band's.";
      if (!roleKey) next.applicantRole = "Choose your role.";
      if (linking && managementOrgs.length > 1 && !linkOrgId)
        next.linkOrgId = "Choose which management company to link.";
      if (!spotifyLink.trim()) next.spotifyLink = "Paste your Spotify artist link.";
      if (!instagram) next.instagram = "Connect your Instagram.";
    } else {
      if (!firstName.trim()) next.applicantFirstName = "Enter your first name.";
      if (!lastName.trim()) next.applicantLastName = "Enter your last name.";
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setFormError("Some details are missing. Check the highlighted fields.");
      return;
    }

    const roster = mode === "roster";
    submit
      .mutateAsync({
        mode,
        accountName: accountName.trim(),
        accountEmail: email.trim(),
        // Signed in, the server reads the applicant off the session; the address is sent only when signed out.
        applicantEmail: !roster && !me ? myEmail.trim() : null,
        applicantRole: roster ? null : roleKey,
        linkOrgId: linking && managementOrgs.length > 1 ? linkOrgId : null,
        applicantFirstName: roster ? firstName.trim() : null,
        applicantLastName: roster ? lastName.trim() : null,
        city: city.trim() || null,
        spotifyLink: roster ? null : spotifyLink.trim(),
        planKey: roster || hidePricing ? null : planKeyFor(product, billing),
        companyType: roster ? companyType : null,
        companyRole: roster ? companyRole : null,
        website: roster ? website.trim() || null : null,
        rosterSize: roster ? rosterSize : null,
        // 02-05 are BAND questions (ART-306): a company is asked only its first section, so it sends none of them.
        genres: roster ? [] : genres,
        traction: roster ? undefined : traction,
        topSocial: roster ? null : topSocial,
        topStreaming: roster ? null : topStreaming,
        team: roster ? [] : team,
        instagramDraft: roster ? null : instagram?.draft ?? null,
        turnstileToken: token,
      })
      .then(() =>
        setSent({
          emails: roster
            ? [email.trim()]
            : [...new Set([email.trim(), (me?.person?.email ?? myEmail).trim()].filter(Boolean))],
        }),
      )
      .catch((err) => {
        // A Turnstile token is single-use: whatever went wrong, the next attempt needs a fresh one.
        setTurnstileReset((n) => n + 1);
        const fieldErrors =
          err instanceof ApiError && err.problem && typeof err.problem === "object"
            ? (err.problem as { errors?: Record<string, string[]> }).errors
            : undefined;
        if (fieldErrors) {
          const mapped: Record<string, string> = {};
          for (const [k, v] of Object.entries(fieldErrors)) if (v?.length) mapped[k] = v[0];
          setErrors(mapped);
          if (mapped.instagram) setInstagram(null);
        }
        setFormError(formErrorMessage(err, "We couldn't send your application. Try again."));
      });
  };

  if (sent) {
    return (
      <div className={styles.form} role="status">
        <h2 className={styles.formTitle}>Application sent.</h2>
        <p className={styles.sentText}>
          Thanks for applying. We review every application personally, and you'll hear back within 48 hours. We sent
          a confirmation to{" "}
          {sent.emails.map((e, i) => (
            <span key={e}>
              {i > 0 && " and "}
              <strong>{e}</strong>
            </span>
          ))}
          .
        </p>
      </div>
    );
  }

  return (
    <>
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <div className={styles.formHead}>
        <h2 className={styles.formTitle}>Your application</h2>
        <div role="tablist" aria-label="Apply as" className={styles.segmented}>
          <SegTab selected={mode === "artist"} onClick={() => setMode("artist")}>Artist / band</SegTab>
          <SegTab selected={mode === "roster"} onClick={() => setMode("roster")}>Label, mgmt or agency</SegTab>
        </div>
      </div>

      {mode === "artist" ? (
        <Section n="01" title="About you">
          <div className={styles.fields}>
            <TextField label="Artist / band name" placeholder="Midnight Static" value={accountName}
              onChange={setAccountName} error={errors.accountName} autoComplete="organization" />
            <TextField label="City, state" placeholder="Nashville, TN" value={city} onChange={setCity}
              autoComplete="address-level2" />
            <div>
              <TextField label="Spotify link" type="url" placeholder="open.spotify.com/artist/…" value={spotifyLink}
                onChange={(v) => {
                  setSpotifyLink(v);
                  setArtist(null);
                }}
                onBlur={onSpotifyBlur} error={errors.spotifyLink ?? artistError ?? undefined} />
              {artist && (
                <p className={styles.artistFound}>
                  <span aria-hidden="true">✓</span> {artist.name}
                  {artist.monthlyListeners != null && ` (${formatListeners(artist.monthlyListeners)} monthly listeners)`}
                </p>
              )}
            </div>
          </div>

          <div className={styles.fields}>
            <TextField label="Band's email" type="email" placeholder="band@yourband.com" value={email} onChange={setEmail}
              error={errors.accountEmail} autoComplete="off"
              hint="The artist account signs in with this, like band@ or booking@." />
            <div className={styles.field}>
              {haveAccount ? (
                <>
                  <span className={styles.label} id="apply-your-account">Your account</span>
                  {me ? (
                    <div className={styles.accountSlot} aria-labelledby="apply-your-account" role="group"
                      data-invalid={errors.applicantEmail ? true : undefined}>
                      {artistSeat ? (
                        // An artist account has no face of its own: its picture is the band's (the PersonAvatar owner
                        // branch needs a mounted band, which /apply never has).
                        <BandAvatar bandId={artistSeat.bandId} avatarMediaId={artistSeat.avatarMediaId}
                          initials={initialsOf(me.person?.displayName || "?")} size="sm" />
                      ) : (
                        <PersonAvatar personId={me.person?.personId} avatarMediaId={me.person?.avatarMediaId}
                          initials={initialsOf(me.person?.displayName || me.person?.email || "?")} size="sm" />
                      )}
                      <span className={styles.accountText}>
                        <span className={styles.accountName}>{me.person?.displayName || me.person?.email}</span>
                        <span className={styles.accountEmail}>{me.person?.email}</span>
                      </span>
                    </div>
                  ) : (
                    <button type="button" className={styles.loginSlot} onClick={() => setLoginOpen(true)}
                      aria-describedby="apply-your-email-hint" data-invalid={errors.applicantEmail ? true : undefined}
                      disabled={signedIn && account.isLoading}>
                      <BrandMark size={20} decorative />
                      <span className={styles.loginSlotText}>{signedIn && account.isLoading ? "Loading…" : "Log in"}</span>
                      <svg className={styles.loginSlotArrow} width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </button>
                  )}
                </>
              ) : (
                <label className={styles.field}>
                  <span className={styles.label}>Your email</span>
                  <input className={styles.input} type="email" placeholder="you@gmail.com" value={myEmail}
                    autoComplete="email" aria-invalid={errors.applicantEmail ? true : undefined}
                    aria-describedby="apply-your-email-hint" onChange={(e) => setMyEmail(e.target.value)} />
                </label>
              )}
              {artistSeat ? (
                <span className={styles.error}>
                  That's the artist account for {artistSeat.displayName || "your band"}. Sign out, then sign in with your
                  own account.
                </span>
              ) : errors.applicantEmail ? (
                <span className={styles.error}>{errors.applicantEmail}</span>
              ) : (
                <span className={styles.hint} id="apply-your-email-hint">
                  The artist and each member need separate accounts, even solo acts.
                </span>
              )}
              <div className={styles.checkLine}>
                {/* Signed in, the account is already the answer: only Sign out is offered. */}
                {!me && (
                  <label className={styles.checkRow}>
                    <input type="checkbox" checked={haveAccount}
                      onChange={(e) => setHaveAccount(e.target.checked)} />
                    <span>I already have an account</span>
                  </label>
                )}
                {me && (
                  <span className={styles.accountActions}>
                    <button type="button" className={styles.inlineLink} onClick={onSignOut}>
                      Sign out
                    </button>
                  </span>
                )}
              </div>
            </div>

            <div className={styles.field}>
              <ApplyPicker label="Your role" value={roleKey} onChange={setRoleKey} placeholder="Choose your role"
                invalid={!!errors.applicantRole}
                options={[
                  { value: BAND_MEMBER_TITLE, label: "Band Member" },
                  ...ORG_ROLES.map((o) => ({ value: o.key, label: o.label })),
                ]} />
              {errors.applicantRole ? (
                <span className={styles.error}>{errors.applicantRole}</span>
              ) : (
                orgRole && (
                  <span className={styles.hint}>{orgRole.label} accounts are created via organization applications.</span>
                )
              )}
              {linking && managementOrgs.length === 1 && (
                <span className={styles.hint}>
                  We'll link <strong>{managementOrgs[0].name}</strong> to the band when it's approved.
                </span>
              )}
              {linking && managementOrgs.length > 1 && (
                <div className={styles.field}>
                  <ApplyPicker label="Management company to link" value={linkOrgId} onChange={setLinkOrgId}
                    placeholder="Choose a company" invalid={!!errors.linkOrgId}
                    options={managementOrgs.map((o) => ({ value: o.orgId, label: o.name }))} />
                  {errors.linkOrgId && <span className={styles.error}>{errors.linkOrgId}</span>}
                </div>
              )}
            </div>
          </div>

          <div className={styles.instagram}>
            <div className={styles.instagramHead}>
              <span className={styles.label}>Verify your Instagram</span>
              <button type="button" className={styles.inlineLink} aria-expanded={whyOpen}
                onClick={() => setWhyOpen((o) => !o)}>
                Why?
              </button>
            </div>
            {whyOpen && (
              <p className={styles.why2}>
                Artist CNTRL places a heavy emphasis on data integrity. We'll use your Instagram verification to
                confirm the band is really yours, and as a bonus, you'll be able to use Artist CNTRL to monitor
                Instagram stats and trends for your account in our dashboard. No Instagram?{" "}
                <a href={`mailto:${contactEmail}`}>Contact us here.</a>
              </p>
            )}
            {instagram ? (
              <div className={styles.igConnected}>
                <span>
                  Connected as <strong>@{instagram.username}</strong> <span aria-hidden="true">✓</span>
                </span>
                <button type="button" className={styles.inlineLink} onClick={onConnectInstagram}>
                  Use a different account
                </button>
              </div>
            ) : (
              <>
                <p className={styles.hint}>Connect the Instagram listed on your Spotify profile.</p>
                <button type="button" className={styles.igButton} onClick={onConnectInstagram}
                  disabled={igStart.isPending || igDraft.isPending}>
                  {igStart.isPending || igDraft.isPending ? "Connecting…" : "Connect Instagram"}
                </button>
              </>
            )}
            {instagram && instagram.matchesListed === false && instagram.listedUsername && (
              <p className={styles.warn}>
                This isn't the Instagram listed for {artist?.name ?? "this artist"}. Connect @{instagram.listedUsername},
                or submit anyway and we'll review it by hand.
              </p>
            )}
            {igError && <p className={styles.error}>{igError}</p>}
            {!igError && errors.instagram && !instagram && <p className={styles.error}>{errors.instagram}</p>}
          </div>

          {!hidePricing && (
          <>
          <div className={styles.groupLabel}>Plan</div>
          <div className={styles.plans}>
            {PLANS.map((p) => (
              <button key={p.key} type="button" aria-pressed={product === p.key} className={styles.planCard}
                data-selected={product === p.key || undefined} onClick={() => setProduct(p.key)}>
                <span className={styles.planTop}>
                  <span className={styles.planName}>{p.name}</span>
                  <span className={styles.planTag}>{billing === "annual" ? "BILLED YEARLY" : "NO COMMIT"}</span>
                </span>
                {/* ART-348: every plan starts on a 14-day free trial. */}
                <span className={styles.planTrialRow}><span className={styles.planTrial}>FIRST 14 DAYS FREE</span></span>
                <span className={styles.planPrice}>
                  {billing === "annual" ? p.annual : p.m2m}
                  <span className={styles.planPer}>/mo</span>
                </span>
                <span className={styles.planNote}>{p.note}</span>
                <span className={styles.planStarts}>$0 TODAY, BILLING STARTS ON DAY 15</span>
              </button>
            ))}
          </div>
          <div className={styles.billingRow}>
            <div className={styles.billing}>
              <SegButton selected={billing === "annual"} light onClick={() => setBilling("annual")}>
                Annual prepay (best value)
              </SegButton>
              <SegButton selected={billing === "m2m"} light onClick={() => setBilling("m2m")}>
                Month-to-month
              </SegButton>
            </div>
            <span className={styles.trialNote}>Your first 14 days are free on either plan. No card needed to start.</span>
          </div>
          </>
          )}
        </Section>
      ) : (
        <Section n="01" title="About your company" lead="For labels, managers and agencies requesting a roster code.">
          <div className={styles.fields}>
            <TextField label="Company name" placeholder="Static Records" value={accountName} onChange={setAccountName}
              error={errors.accountName} autoComplete="organization" />
            <TextField label="Contact email" type="email" placeholder="you@label.com" value={email} onChange={setEmail}
              error={errors.accountEmail} autoComplete="email" />
            <TextField label="First name" value={firstName} onChange={setFirstName} error={errors.applicantFirstName}
              autoComplete="given-name" />
            <TextField label="Last name" value={lastName} onChange={setLastName} error={errors.applicantLastName}
              autoComplete="family-name" />
            <ApplyPicker label="Company type" value={companyType} onChange={setCompanyType}
              options={COMPANY_TYPES.map((c) => ({ value: c.key, label: c.label }))} />
            <ApplyPicker label="Your role" value={companyRole} onChange={setCompanyRole}
              options={COMPANY_ROLES.map((o) => ({ value: o, label: o }))} />
            <TextField label="City, state" placeholder="Los Angeles, CA" value={city} onChange={setCity}
              autoComplete="address-level2" />
            <TextField label="Website" optional placeholder="label.com" value={website} onChange={setWebsite} />
          </div>
          <div className={styles.groupLabel}>Roster size</div>
          <div className={styles.chips}>
            {ROSTER_SIZES.map((r) => (
              <Chip key={r} selected={rosterSize === r} onClick={() => setRosterSize(r)}>{`${r} artists`}</Chip>
            ))}
          </div>
        </Section>
      )}

      {/* 02-05 ask about the BAND (ART-306). A label, management company or agency answers only its first
          section; everything below is the artist's. */}
      {mode === "artist" && (
      <>
      <Section n="02" title="Pick up to 3 genres/sub-genres" divided
        aside={<span className={styles.count}>{genres.length} / 3 SELECTED</span>}>
        <GenrePicker value={genres} onChange={setGenres} />
      </Section>

      <Section n="03" title="Traction" lead="No wrong answers. This just helps us size the fit." divided>
        <div className={styles.traction}>
          {TRACTION.map((t) => (
            <div key={t.key} className={styles.tractionCard}>
              <div className={styles.tractionQ}>{t.q}</div>
              <div className={styles.tractionOpts}>
                {t.opts.map((o) => (
                  <Chip key={o} small light selected={traction[t.key] === o}
                    onClick={() => setTraction((prev) => ({ ...prev, [t.key]: o }))}>
                    {o}
                  </Chip>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section n="04" title="Where you're winning" lead="So we know where to plug in first." divided>
        <div className={styles.fields}>
          <ApplyPicker label="Top social platform" value={topSocial} onChange={setTopSocial}
            options={SOCIALS.map((o) => ({ value: o, label: o }))} />
          <ApplyPicker label="Top streaming platform" value={topStreaming} onChange={setTopStreaming}
            options={STREAMING.map((o) => ({ value: o, label: o }))} />
        </div>
      </Section>

      <Section n="05" title="Who's on the team" lead="Check all that apply. Every one of them gets a seat." divided>
        <div className={styles.chips}>
          {TEAM.map((m) => {
            const on = team.includes(m);
            return (
              <button key={m} type="button" aria-pressed={on} className={styles.chip} data-selected={on || undefined}
                onClick={() => setTeam((prev) => (on ? prev.filter((x) => x !== m) : [...prev, m]))}>
                <span className={styles.tick} aria-hidden="true">{on ? "✓" : ""}</span>
                {m}
              </button>
            );
          })}
        </div>
      </Section>
      </>
      )}

      {!hidePricing && (
        <div className={styles.summaryRow}>
          <div className={styles.summary}>
            <span className={styles.summaryLabel}>YOUR PLAN</span>
            <span className={styles.summaryValue}>{summary}</span>
          </div>
        </div>
      )}

      {config.data?.turnstileSiteKey && (
        <div className={styles.turnstile}>
          <Turnstile siteKey={config.data.turnstileSiteKey} onToken={setToken} resetKey={turnstileReset} />
        </div>
      )}

      {formError && <p className={styles.formError} role="alert">{formError}</p>}

      <div className={styles.submitRow}>
        <span className={styles.submitNote}>We review every application personally. You'll hear back within 48 hours</span>
        <button type="submit" className={styles.submit} disabled={submit.isPending}>
          {submit.isPending ? "Sending…" : "Submit application"}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </form>

    {/* Outside the form on purpose: the log-in has its own <form>, and a React submit bubbles through the
        component tree, so nested inside it would also submit the application. */}
    {loginOpen && (
      <Modal title="Log in" size="sm" onClose={() => setLoginOpen(false)}>
        <LoginScreen
          embedded
          onAuthenticated={onSignedIn}
          // A new tab: leaving this page would lose the application typed so far.
          onForgotPassword={() => window.open("/forgot-password", "_blank", "noopener")}
        />
      </Modal>
    )}
    </>
  );
}

// ── pieces ─────────────────────────────────────────────────────────────────────────────────────────────────

function Section({ n, title, lead, aside, divided, children }: {
  n: string; title: string; lead?: string; aside?: ReactNode; divided?: boolean; children: ReactNode;
}) {
  return (
    <section className={styles.section} data-divided={divided || undefined}>
      <div className={styles.sectionHead}>
        <div className={styles.sectionTitleRow}>
          <span className={styles.sectionNum}>{n}</span>
          <h3 className={styles.sectionTitle}>{title}</h3>
        </div>
        {aside}
      </div>
      {lead && <p className={styles.sectionLead}>{lead}</p>}
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

function TextField({ label, value, onChange, onBlur, placeholder, type = "text", error, hint, optional, autoComplete }: {
  label: string; value: string; onChange: (v: string) => void; onBlur?: () => void; placeholder?: string;
  type?: string; error?: string; hint?: string; optional?: boolean; autoComplete?: string;
}) {
  // The hint and the error sit OUTSIDE the <label>: inside it they would become part of the field's accessible name.
  return (
    <div className={styles.field}>
      <label className={styles.field}>
        <span className={styles.label}>
          {label}
          {optional && <span className={styles.optional}> (optional)</span>}
        </span>
        <input
          className={styles.input}
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete ?? "off"}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      </label>
      {hint && !error && <span className={styles.hint}>{hint}</span>}
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}

function SegTab({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" role="tab" aria-selected={selected} className={styles.segTab}
      data-selected={selected || undefined} onClick={onClick}>
      {children}
    </button>
  );
}

function SegButton({ selected, onClick, light, children }: {
  selected: boolean; onClick: () => void; light?: boolean; children: ReactNode;
}) {
  return (
    <button type="button" aria-pressed={selected} className={light ? styles.billBtn : styles.segTab}
      data-selected={selected || undefined} onClick={onClick}>
      {children}
    </button>
  );
}

function Chip({ selected, onClick, small, light, children }: {
  selected: boolean; onClick: () => void; small?: boolean; light?: boolean; children: ReactNode;
}) {
  return (
    <button type="button" aria-pressed={selected} className={styles.chip} data-small={small || undefined}
      data-light={light || undefined} data-selected={selected || undefined} onClick={onClick}>
      {children}
    </button>
  );
}

function Footer({ hidePricing }: { hidePricing: boolean }) {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <img src={logoWhite} alt="Artist CNTRL" className={styles.footerLogo} />
            <p className={styles.footerTagline}>
              The ecosystem for working musicians and the people who manage them. Built by touring artists.
            </p>
            <div className={styles.socials}>
              <a href={INSTAGRAM_URL} aria-label="Instagram" className={styles.social}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.2" cy="6.8" r="0.6" fill="currentColor" />
                </svg>
              </a>
              <a href={TIKTOK_URL} aria-label="TikTok" className={styles.social}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 4v10.5a3.5 3.5 0 1 1-3.5-3.5" />
                  <path d="M14 4c.5 2.6 2.4 4.3 5 4.5" />
                </svg>
              </a>
              <a href={ANTENNA_URL} aria-label="Antenna" className={styles.social}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="11" r="2" />
                  <path d="M8 7a5.6 5.6 0 0 0 0 8M16 7a5.6 5.6 0 0 1 0 8M5 4.5a9.5 9.5 0 0 0 0 13M19 4.5a9.5 9.5 0 0 1 0 13M12 13v8" />
                </svg>
              </a>
            </div>
          </div>
          <div className={styles.footerCols}>
            <FooterCol title="PRODUCT" links={[
              ["Features", `${MARKETING_HOME}#everything`], ["Integrations", `${MARKETING_HOME}#integrations`],
              ["Antenna fan app", ANTENNA_URL],
              // ART-346: no page that sells the subscription is linked from the form the iOS app opens outside the
              // US storefront — the pricing section, or the Pro page.
              ...(hidePricing
                ? []
                : ([["Pricing", `${MARKETING_HOME}#pricing`], ["Artist CNTRL Pro", `${MARKETING_HOME}pro`]] as const)),
            ]} />
            <FooterCol title="FOR" links={[
              ["Artists", `${MARKETING_HOME}#everything`], ["Managers", `${MARKETING_HOME}#everything`],
              ["Labels", `${MARKETING_HOME}#everything`], ["Booking", `${MARKETING_HOME}#everything`],
              ["Crew", `${MARKETING_HOME}#everything`],
            ]} />
            <FooterCol title="COMPANY" links={[
              ["Apply", "#apply"], ["Log in", "/login"], ["Contact", "/support"], ["Antenna", ANTENNA_URL],
            ]} />
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span className={styles.footerLegalLeft}>
            <span>© {new Date().getFullYear()} Artist CNTRL. Made in Nashville, TN</span>
            <span className={styles.productOf}>
              A product of <img src={cntrlGlobal} alt="CNTRL Global" className={styles.cntrlGlobal} />
            </span>
          </span>
          <span className={styles.footerLegalRight}>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: ReadonlyArray<readonly [string, string]> }) {
  return (
    <div className={styles.footerCol}>
      <div className={styles.footerColTitle}>{title}</div>
      {links.map(([label, href]) => (
        <a key={label} href={href}>{label}</a>
      ))}
    </div>
  );
}

/**
 * The shared Picker (the app's listbox on desktop, its bottom sheet on a touch device) in this page's own field
 * look: the label above in the page's style and the trigger restyled to match the text inputs beside it.
 */
function ApplyPicker({ label, value, onChange, options, placeholder, invalid }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  placeholder?: string;
  invalid?: boolean;
}) {
  return (
    <div className={styles.field}>
      <span className={styles.label} aria-hidden="true">{label}</span>
      <Picker bare label={label} value={value} onChange={onChange} options={options} placeholder={placeholder}
        triggerClassName={invalid ? `${styles.picker} ${styles.pickerInvalid}` : styles.picker} />
    </div>
  );
}

function initialsOf(name: string): string {
  const parts = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function formatListeners(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, "")}K`;
  return String(n);
}

/** The handoff's three families (Archivo with its width axis, Schibsted Grotesk, JetBrains Mono), loaded for this
 * page only — the app's own self-hosted fonts are a different set. */
function useHandoffFonts() {
  useEffect(() => {
    if (document.querySelector("link[data-apply-fonts]")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = APPLY_FONTS_CSS;
    link.dataset.applyFonts = "true";
    document.head.appendChild(link);
  }, []);
}
